import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { getTokenImage } from '@/ui/assets/tokens'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { type Address, getAddress } from 'viem'
import { usePublicClient } from 'wagmi'
import { fetchTokenOnchain, fetchTokensOnchain } from './onchainTokenFetcher'
import { eulerTokensQueryKey, setTokenInCache, useEulerIndexerTokens } from './tokensQuery'
import type { NormalizedTokenInfo } from './types'

/**
 * Hook to get token info with indexer-first, onchain-fallback pattern
 *
 * Priority:
 * 1. Check Euler Indexer cache (React Query cache)
 * 2. If not found, fetch from onchain and cache the result
 *
 * This ensures we always have token data while preferring the faster indexer
 */
export function useTokenInfo(chainId: number, address: Address | undefined) {
  const queryClient = useQueryClient()
  const publicClient = usePublicClient({ chainId })

  // First, try to get from Euler Indexer cache
  const { data: tokenMap, isLoading: isIndexerLoading, isError: isIndexerError } = useEulerIndexerTokens(chainId)

  // Check if token exists in indexer cache
  const indexerToken = address ? tokenMap?.get(getAddress(address) as Address) : undefined

  // Only fetch from onchain if:
  // 1. We have an address and publicClient
  // 2. Indexer is not loading AND (indexer has error OR token not found in cache)
  const shouldFetchOnchain = Boolean(address && publicClient && !isIndexerLoading && (isIndexerError || !indexerToken))

  const {
    data: onchainToken,
    isLoading: isOnchainLoading,
    isError: isOnchainError,
  } = useQuery({
    queryKey: ['token-onchain', chainId, address],
    queryFn: async () => {
      if (!address || !publicClient) throw new Error('No address or publicClient provided')

      const token = await fetchTokenOnchain(publicClient, chainId, address)

      // Cache the onchain result in the indexer token map for future use
      setTokenInCache(queryClient, chainId, token)

      return token
    },
    enabled: shouldFetchOnchain,
    staleTime: 10 * 60 * 1000, // 10 minutes for onchain data
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
  })

  // Return indexer data if available, otherwise onchain data
  const token = indexerToken || onchainToken

  return {
    token,
    isLoading: isIndexerLoading || (shouldFetchOnchain && isOnchainLoading),
    isError: !token && (isIndexerError || isOnchainError),
    source: indexerToken ? ('indexer' as const) : onchainToken ? ('onchain' as const) : undefined,
  }
}

/**
 * Hook to get multiple tokens with indexer-first, onchain-fallback pattern
 */
export function useTokensInfo(chainId: number, addresses: Address[]) {
  const queryClient = useQueryClient()
  const publicClient = usePublicClient({ chainId })

  // First, try to get from Euler Indexer cache
  const { data: tokenMap, isPending: isPendingIndexer, isError: isIndexerError } = useEulerIndexerTokens(chainId)

  // Find which tokens are missing from cache
  const missingAddresses = useMemo(
    () =>
      addresses.filter((address) => {
        if (!tokenMap) return true
        try {
          return !tokenMap.has(getAddress(address) as Address)
        } catch {
          return true
        }
      }),
    [addresses, tokenMap],
  )

  // Fetch missing tokens from onchain
  const shouldFetchOnchain = !isPendingIndexer && missingAddresses.length > 0 && !!publicClient

  const { data: onchainTokens, isPending: isPendingOnchain } = useQuery({
    queryKey: ['tokens-onchain', chainId, missingAddresses],
    queryFn: async () => {
      if (!publicClient) throw new Error('No publicClient provided')

      const tokens = await fetchTokensOnchain(publicClient, chainId, missingAddresses)

      // Cache all fetched tokens
      for (const token of tokens.values()) {
        setTokenInCache(queryClient, chainId, token)
      }

      return tokens
    },
    enabled: shouldFetchOnchain,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 2,
  })

  // Combine indexer and onchain results
  const tokens = useMemo(() => {
    const tokens = new Map<Address, NormalizedTokenInfo>()

    for (const address of addresses) {
      try {
        const normalizedAddress = getAddress(address) as Address

        // Check indexer first
        const indexerToken = tokenMap?.get(normalizedAddress)
        if (indexerToken) {
          tokens.set(normalizedAddress, indexerToken)
          continue
        }

        // Check onchain fallback
        const onchainToken = onchainTokens?.get(normalizedAddress)
        if (onchainToken) {
          tokens.set(normalizedAddress, onchainToken)
        }
      } catch {
        // Invalid address, skip
      }
    }

    return tokens
  }, [addresses, onchainTokens, tokenMap])

  return {
    tokens,
    isPending: isPendingIndexer || (shouldFetchOnchain && isPendingOnchain),
    isIndexerError,
    missingCount: addresses.length - tokens.size,
  }
}

/**
 * Helper to get token from a tokens map by address
 * Handles address normalization and error handling
 */
export function getTokenFromMap(
  tokens: Map<Address, NormalizedTokenInfo>,
  address: Address | undefined,
): NormalizedTokenInfo | undefined {
  if (!address) return undefined
  try {
    return tokens.get(getAddress(address) as Address)
  } catch {
    return undefined
  }
}

/**
 * Get token image URL with local-first, indexer-fallback
 * Uses local token images as priority, falls back to Euler indexer logoURI
 */
export function getTokenImageUrl(tokenInfo: NormalizedTokenInfo | undefined, fallbackSymbol?: string): string {
  // Priority 1: Use local token image if symbol is available
  const symbol = tokenInfo?.symbol || fallbackSymbol
  if (symbol) {
    return getTokenImage(TokenSymbol(symbol))
  }

  // Priority 2: Fall back to indexer logoURI if available
  if (tokenInfo?.logoURI) {
    return tokenInfo.logoURI
  }

  // Default fallback
  return getTokenImage(TokenSymbol('UNKNOWN'))
}

/**
 * Hook to get token image URL with local-first, indexer-fallback
 */
export function useTokenImageUrl(chainId: number, address: Address | undefined, fallbackSymbol?: string): string {
  const { token } = useTokenInfo(chainId, address)
  return getTokenImageUrl(token, fallbackSymbol)
}

/**
 * Get token symbol with indexer-first, onchain-fallback
 * Convenience hook for common use case
 */
export function useTokenSymbol(chainId: number, address: Address | undefined): string | undefined {
  const { token } = useTokenInfo(chainId, address)
  return token?.symbol
}

/**
 * Get token decimals with indexer-first, onchain-fallback
 * Convenience hook for common use case
 */
export function useTokenDecimals(chainId: number, address: Address | undefined): number | undefined {
  const { token } = useTokenInfo(chainId, address)
  return token?.decimals
}

/**
 * Imperatively fetch token info (for use outside React components)
 * Checks cache first, then fetches from onchain if needed
 */
export async function getTokenInfo(
  queryClient: ReturnType<typeof useQueryClient>,
  publicClient: ReturnType<typeof usePublicClient>,
  chainId: number,
  address: Address,
): Promise<NormalizedTokenInfo> {
  // Check cache first
  const tokenMap = queryClient.getQueryData<Map<Address, NormalizedTokenInfo>>(eulerTokensQueryKey(chainId))

  if (tokenMap) {
    try {
      const normalizedAddress = getAddress(address) as Address
      const cachedToken = tokenMap.get(normalizedAddress)
      if (cachedToken) {
        return cachedToken
      }
    } catch {
      // Invalid address
    }
  }

  if (!publicClient) {
    throw new Error('No publicClient provided')
  }

  // Fetch from onchain
  const token = await fetchTokenOnchain(publicClient, chainId, address)

  // Cache the result
  setTokenInCache(queryClient, chainId, token)

  return token
}
